import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import SchoolIcon from '@mui/icons-material/School';
import ScienceIcon from '@mui/icons-material/Science';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import UniversityLogo from '../UniversityLogo/UniversityLogo';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(25, 118, 210, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(156, 39, 176, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 6 }}>
        {/* Header with Logo */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 6,
          }}
        >
          <UniversityLogo size={150} />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mt: 3,
              mb: 1,
              fontWeight: 700,
              color: theme.palette.text.primary,
              textAlign: 'center',
            }}
          >
            زانكوى سه لاحه دین - هه ولير
          </Typography>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textAlign: 'center',
            }}
          >
            Salahaddin University-Erbil
          </Typography>
        </Box>

        {/* Main Content Card */}
        <Paper
          elevation={8}
          sx={{
            p: 5,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#f5f5f5', 0.95)} 100%)`,
            backdropFilter: 'blur(10px)',
            mb: 4,
          }}
        >
          {/* Title Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <ScienceIcon
              sx={{
                fontSize: 60,
                color: theme.palette.primary.main,
                mb: 2,
              }}
            />
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: theme.palette.primary.main,
                lineHeight: 1.2,
              }}
            >
              An Energy-Efficient Service Composition Mechanism Using a Hybrid Meta-Heuristic Algorithm in a Mobile Cloud Environment
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
                mt: 2,
              }}
            >
              A Dissertation
            </Typography>
          </Box>

          {/* Information Grid */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Author Information */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon
                      sx={{
                        fontSize: 40,
                        color: theme.palette.primary.main,
                        mr: 2,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Author
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Godar Jader Ibrahim
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    M.Sc. in Software Engineering (SE) (2012)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The University of Salahaddin - Erbil
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Supervisors Information */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SupervisorAccountIcon
                      sx={{
                        fontSize: 40,
                        color: theme.palette.secondary.main,
                        mr: 2,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Supervised by
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Prof. Tarik Ahmed Rashid
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Dr. Mobayode O. Akinsolu
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Degree Information */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SchoolIcon
                      sx={{
                        fontSize: 40,
                        color: theme.palette.success.main,
                        mr: 2,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Degree
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Doctor of Philosophy in Software Engineering
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    College of Engineering
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Date and Location */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarTodayIcon
                      sx={{
                        fontSize: 40,
                        color: theme.palette.warning.main,
                        mr: 2,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Date & Location
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      September 2025
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOnIcon
                      sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Erbil, KURDISTAN
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Description Section */}
          <Box
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 2,
              background: alpha(theme.palette.primary.main, 0.05),
              borderLeft: `4px solid ${theme.palette.primary.main}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              About This Research
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              This dissertation presents a novel approach to energy-efficient service composition
              in mobile cloud environments using hybrid meta-heuristic algorithms. The research
              combines the strengths of multiple optimization techniques to achieve optimal
              service composition while minimizing energy consumption.
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The system provides an interactive platform for researchers and practitioners to
              experiment with different optimization parameters, visualize results, and analyze
              the performance of various meta-heuristic algorithms in service composition scenarios.
            </Typography>
          </Box>

          {/* Login Button */}
          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<LoginIcon />}
              onClick={handleLoginClick}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: 4,
                '&:hover': {
                  boxShadow: 8,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s',
              }}
            >
              Access the System
            </Button>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Salahaddin University-Erbil. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingPage;

